import React from 'react'

import {
  getFreeTextAnswerText,
  isAnsweredNa,
  transformProductPrivacyIssue,
} from '@/utils/productPrivacyIssue'
import { ProductPrivacyIssueAnswer } from '@/utils/productPrivacyIssue/types'

import ResultsCard from '../ResultsCard'

import {
  COLOR_STATUS,
  Props,
} from './types'

const getColor = (isClear: boolean, status: COLOR_STATUS, hasAnswers: boolean, code: string, type: string, answers: ProductPrivacyIssueAnswer[], isNotApplicable: boolean) => {
  if (!isClear) {
    return 'orange'
  }

  if (code === '2.1.2' && hasAnswers) {
    return 'green'
  }

  if (isNotApplicable) {
    return 'grey'
  }

  if (type === 'free-text') {
    if (!hasAnswers) {
      return 'blue'
    }
    const hasEmptyAnswers = answers.some(answer => !answer.userEnteredValue || answer.userEnteredValue === '[]')
    if (hasEmptyAnswers) {
      return 'blue'
    }
  }

  switch (status) {
    case 'good':
      return 'green'
    case 'bad':
      return 'blue'
    default:
      return 'blue'
  }
}

const getAnswer = (isClear: boolean, isNotApplicable: boolean) => {
  if (isNotApplicable) {
    return 'N/A'
  }

  return isClear ? 'Clear' : 'Unclear'
}

// disabling this for now as we might revert back to disclose practices when score is 0
const getPracticesButtonText = ({ hasAnswers }: {hasAnswers: boolean}) => {
  if (hasAnswers) {
    return 'Update Practices'
  }

  return 'Disclose Practices'
}

const getUrl = (maxScore: number, productPrivacyIssueId: string) => `/dashboard/privacy-issues/${productPrivacyIssueId}`

function PrivacyIssueCard({
  data,
  isList = true,
  hasUrl = true,
  onPracticesClick,
  hasPracticesButton,
}: Props): React.ReactElement {
  const {
    id,
    code,
    answerStatement,
    isClear,
    importanceLevel,
    score,
    maxScore,
    question,
    status,
    tags,
    answers,
    type,
  } = transformProductPrivacyIssue(data)

  const isNotApplicable = isAnsweredNa(answers)
  const color = getColor(isClear, status, !!answers.length, code, type, answers, isNotApplicable)
  const practicesButtonText = hasPracticesButton ? getPracticesButtonText({ hasAnswers: isClear }) : ''
  const url = hasUrl ? getUrl(maxScore, id) : undefined
  const numRecommendedActions = (data?.answers && data?.answers?.length > 0 && score < maxScore && !isNotApplicable && data?.privacyIssue?.privacyIssueRecommendedActions?.length) || 0

  // answered options without a statement (e.g. free-text) fall back to the question
  let transformedIssueDesc = isClear && answerStatement ? answerStatement : question
  if (code === '2.1.2') {
    const areAllAnswersNo = answers.every(answer => answer.answer?.internalValue === 'No')
    if (areAllAnswersNo) {
      transformedIssueDesc = 'The product does not collect any categories of personally identifiable information.'
    }
    else {
      const collectedPIIs = answers
        .filter(answer => answer.answer?.internalValue === 'Yes')
        .map(answer => answer.answer?.value).join(', ')
      transformedIssueDesc = `The product collects the following categories of personally identifiable information: ${collectedPIIs}.`
    }
  }
  else if (type === 'free-text') {
    // like a statement, the entered text replaces the question once answered
    const hasEmptyAnswers = answers.some(answer => !answer.userEnteredValue || answer.userEnteredValue === '[]')
    const freeTextAnswer = getFreeTextAnswerText(answers)
    transformedIssueDesc = !hasEmptyAnswers && freeTextAnswer ? freeTextAnswer : question
  }

  return (
    <ResultsCard
      issueNum={code}
      color={color}
      issueDesc={transformedIssueDesc}
      categories={tags}
      score={score}
      answer={getAnswer(isClear, isNotApplicable)}
      importance={importanceLevel}
      isList={isList}
      url={url}
      numRecommendedActions={numRecommendedActions}
      practicesButtonText={practicesButtonText}
      onPracticesClick={onPracticesClick}
    />
  )
}

export default React.memo(PrivacyIssueCard)
